import React, { useState, useEffect } from 'react';
import { 
  Brain, TrendingUp, ThumbsUp, ThumbsDown, Target, AlertTriangle,
  RefreshCw, CheckCircle2, XCircle, Clock, Lightbulb, BarChart3,
  ArrowUpRight, ArrowDownRight, Minus
} from 'lucide-react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../App';
import { toast } from 'sonner';

import { BACKEND_URL } from "../config/api";

export default function AILearningDashboardPage() {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [batchValidating, setBatchValidating] = useState(false);

  const MANAGEMENT_ROLES = ['admin', 'ceo', 'hod_sales', 'sales_head'];
  const hasAccess = MANAGEMENT_ROLES.includes(user?.role);

  useEffect(() => {
    if (hasAccess) {
      fetchData();
    }
  }, [hasAccess]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [dashRes, accRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/ai-learning/dashboard`, { headers }),
        fetch(`${BACKEND_URL}/api/ai-learning/accuracy?days=30`, { headers })
      ]);

      if (dashRes.ok) setDashboard(await dashRes.json());
      if (accRes.ok) setAccuracy(await accRes.json());
    } catch (err) {
      console.error('Failed to fetch AI learning data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBatchValidate = async () => {
    setBatchValidating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/ai-learning/batch-validate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Validated ${data.validated_count} predictions`);
        fetchData();
      }
    } catch (err) {
      toast.error('Batch validation failed');
    } finally {
      setBatchValidating(false);
    }
  };

  if (!hasAccess) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center min-h-[60vh]">
          <Card className="bg-white border-slate-200 max-w-md">
            <CardContent className="p-8 text-center">
              <Brain className="w-12 h-12 mx-auto text-violet-500 mb-4" />
              <h2 className="text-xl font-bold text-slate-900 font-['Manrope'] mb-2">Admin Access Required</h2>
              <p className="text-slate-600">AI Learning Dashboard is available for management roles only.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  const getAccuracyColor = (rate) => {
    if (rate >= 70) return 'text-emerald-600';
    if (rate >= 50) return 'text-amber-600';
    return 'text-red-600';
  };

  const getAccuracyBg = (rate) => {
    if (rate >= 70) return 'bg-emerald-500';
    if (rate >= 50) return 'bg-amber-500';
    return 'bg-red-500';
  };

  const getTrendIcon = (trend) => {
    if (!trend || trend.length < 2) return <Minus className="w-4 h-4 text-slate-400" />;
    const last = trend[trend.length - 1]?.accuracy_rate;
    const prev = trend[trend.length - 2]?.accuracy_rate;
    if (!last || !prev) return <Minus className="w-4 h-4 text-slate-400" />;
    if (last > prev) return <ArrowUpRight className="w-4 h-4 text-emerald-500" />;
    if (last < prev) return <ArrowDownRight className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-slate-400" />;
  };

  const featureLabels = {
    lead_scoring: 'Lead Scoring',
    next_action: 'Next Best Action',
    stage_prediction: 'Stage Prediction',
    project_fit: 'Project Fit',
    nurture_journey: 'Nurture Journey',
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="ai-learning-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope'] flex items-center gap-2">
              <Brain className="w-7 h-7 text-violet-500" />
              AI Learning Dashboard
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Track AI accuracy, collect feedback, and monitor continuous improvement
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleBatchValidate}
              disabled={batchValidating}
              className="border-slate-200"
              data-testid="batch-validate-btn"
            >
              {batchValidating ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              Validate Pending
            </Button>
            <Button onClick={fetchData} variant="outline" className="border-slate-200">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading AI learning data...</div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Overall Accuracy */}
              <Card className="border-slate-200 bg-gradient-to-br from-violet-50 to-white">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-slate-600">Overall Accuracy</span>
                    {getTrendIcon(dashboard?.accuracy_trend)}
                  </div>
                  <div className="flex items-end gap-2">
                    <span className={`text-3xl font-bold ${getAccuracyColor(accuracy?.overall?.accuracy_rate)}`}>
                      {accuracy?.overall?.accuracy_rate ?? '--'}%
                    </span>
                  </div>
                  <Progress 
                    value={accuracy?.overall?.accuracy_rate || 0} 
                    className="mt-3 h-2"
                  />
                  <p className="text-xs text-slate-500 mt-2">
                    {accuracy?.overall?.accurate_predictions || 0} / {accuracy?.overall?.total_predictions || 0} correct
                  </p>
                </CardContent>
              </Card>

              {/* Total Predictions */}
              <Card className="border-slate-200">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-slate-600">Total Predictions</span>
                    <Target className="w-5 h-5 text-blue-500" />
                  </div>
                  <span className="text-3xl font-bold text-slate-900">
                    {dashboard?.summary?.total_predictions?.toLocaleString() || 0}
                  </span>
                  <p className="text-xs text-slate-500 mt-2">
                    {dashboard?.summary?.predictions_7d || 0} this week
                  </p>
                </CardContent>
              </Card>

              {/* Pending Validation */}
              <Card className="border-slate-200">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-slate-600">Pending Validation</span>
                    <Clock className="w-5 h-5 text-amber-500" />
                  </div>
                  <span className="text-3xl font-bold text-amber-600">
                    {dashboard?.summary?.pending_count?.toLocaleString() || 0}
                  </span>
                  <p className="text-xs text-slate-500 mt-2">
                    {dashboard?.summary?.validation_rate || 0}% validated
                  </p>
                </CardContent>
              </Card>

              {/* User Satisfaction */}
              <Card className="border-slate-200">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-slate-600">User Satisfaction</span>
                    <ThumbsUp className="w-5 h-5 text-emerald-500" />
                  </div>
                  <span className={`text-3xl font-bold ${getAccuracyColor(accuracy?.feedback?.satisfaction_rate)}`}>
                    {accuracy?.feedback?.satisfaction_rate ?? '--'}%
                  </span>
                  <p className="text-xs text-slate-500 mt-2">
                    {accuracy?.feedback?.positive || 0} positive / {accuracy?.feedback?.negative || 0} negative
                  </p>
                </CardContent>
              </Card>
            </div>

            <Tabs defaultValue="accuracy" className="space-y-4">
              <TabsList className="bg-slate-100">
                <TabsTrigger value="accuracy">Accuracy by Feature</TabsTrigger>
                <TabsTrigger value="trend">Accuracy Trend</TabsTrigger>
                <TabsTrigger value="feedback">Recent Feedback</TabsTrigger>
                <TabsTrigger value="learnings">Active Learnings</TabsTrigger>
              </TabsList>

              {/* Accuracy by Feature */}
              <TabsContent value="accuracy">
                <Card className="border-slate-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-semibold flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-violet-500" />
                      Accuracy by AI Feature
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {accuracy?.by_feature?.length > 0 ? (
                        accuracy.by_feature.map((feat, idx) => (
                          <div key={idx} className="flex items-center gap-4">
                            <div className="w-40 text-sm font-medium text-slate-700">
                              {featureLabels[feat.feature] || feat.feature}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <Progress 
                                  value={feat.accuracy_rate} 
                                  className={`h-3 flex-1`}
                                />
                                <span className={`text-sm font-semibold w-12 text-right ${getAccuracyColor(feat.accuracy_rate)}`}>
                                  {feat.accuracy_rate}%
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-slate-500 w-48">
                              <span>{feat.total_predictions} predictions</span>
                              <span className="flex items-center gap-1 text-emerald-600">
                                <ThumbsUp className="w-3 h-3" /> {feat.positive_feedback}
                              </span>
                              <span className="flex items-center gap-1 text-red-500">
                                <ThumbsDown className="w-3 h-3" /> {feat.negative_feedback}
                              </span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-slate-500 text-center py-8">No accuracy data yet. Predictions will be validated as leads progress.</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Accuracy Trend */}
              <TabsContent value="trend">
                <Card className="border-slate-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-semibold flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-violet-500" />
                      Accuracy Trend (Last 5 Weeks)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-end justify-between gap-4 h-48 px-4">
                      {dashboard?.accuracy_trend?.map((week, idx) => (
                        <div key={idx} className="flex flex-col items-center flex-1">
                          <div 
                            className={`w-full rounded-t-lg ${getAccuracyBg(week.accuracy_rate)} transition-all`}
                            style={{ height: `${week.accuracy_rate || 5}%`, minHeight: '8px' }}
                          />
                          <div className="text-xs font-medium text-slate-600 mt-2">{week.week}</div>
                          <div className={`text-sm font-bold ${getAccuracyColor(week.accuracy_rate)}`}>
                            {week.accuracy_rate ?? '--'}%
                          </div>
                          <div className="text-xs text-slate-400">n={week.total}</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Recent Feedback */}
              <TabsContent value="feedback">
                <Card className="border-slate-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-semibold flex items-center gap-2">
                      <ThumbsUp className="w-5 h-5 text-violet-500" />
                      Recent User Feedback
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {dashboard?.recent_feedback?.length > 0 ? (
                        dashboard.recent_feedback.map((fb, idx) => (
                          <div 
                            key={idx} 
                            className={`p-3 rounded-lg border ${fb.rating === 'positive' ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2">
                                {fb.rating === 'positive' ? (
                                  <ThumbsUp className="w-4 h-4 text-emerald-500" />
                                ) : (
                                  <ThumbsDown className="w-4 h-4 text-red-500" />
                                )}
                                <Badge variant="outline" className="text-xs">
                                  {featureLabels[fb.feature] || fb.feature}
                                </Badge>
                              </div>
                              <span className="text-xs text-slate-500">
                                {fb.user_name} • {new Date(fb.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            {fb.comment && (
                              <p className="text-sm text-slate-700 mt-2">{fb.comment}</p>
                            )}
                          </div>
                        ))
                      ) : (
                        <p className="text-slate-500 text-center py-8">No feedback collected yet.</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Active Learnings */}
              <TabsContent value="learnings">
                <Card className="border-slate-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-semibold flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-amber-500" />
                      Active AI Learnings
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {dashboard?.active_learnings?.length > 0 ? (
                        dashboard.active_learnings.map((learning, idx) => (
                          <div 
                            key={idx} 
                            className={`p-4 rounded-lg border ${
                              learning.action_needed 
                                ? 'bg-red-50 border-red-200' 
                                : learning.confidence === 'high'
                                  ? 'bg-emerald-50 border-emerald-200'
                                  : 'bg-amber-50 border-amber-200'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              {learning.action_needed ? (
                                <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                              ) : (
                                <Lightbulb className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                              )}
                              <div className="flex-1">
                                <p className="text-sm text-slate-800">{learning.insight}</p>
                                <div className="flex items-center gap-3 mt-2">
                                  <Badge 
                                    variant="outline" 
                                    className={`text-xs ${
                                      learning.confidence === 'high' 
                                        ? 'border-emerald-300 text-emerald-700' 
                                        : learning.confidence === 'medium'
                                          ? 'border-amber-300 text-amber-700'
                                          : 'border-slate-300 text-slate-600'
                                    }`}
                                  >
                                    {learning.confidence} confidence
                                  </Badge>
                                  {learning.data_points && (
                                    <span className="text-xs text-slate-500">
                                      Based on {learning.data_points} data points
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8">
                          <Lightbulb className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                          <p className="text-slate-500">No learnings generated yet.</p>
                          <p className="text-xs text-slate-400 mt-1">
                            Learnings will appear as more predictions are validated.
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            {/* Recent Misses */}
            {dashboard?.recent_misses?.length > 0 && (
              <Card className="border-slate-200 border-red-100">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-semibold flex items-center gap-2 text-red-700">
                    <XCircle className="w-5 h-5" />
                    Recent Incorrect Predictions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {dashboard.recent_misses.slice(0, 5).map((miss, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100">
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className="text-xs border-red-200 text-red-600">
                            {featureLabels[miss.feature] || miss.feature}
                          </Badge>
                          <span className="text-sm text-slate-700">Lead: {miss.lead_id}</span>
                        </div>
                        <div className="text-xs text-slate-500">
                          Predicted: {miss.prediction?.score || miss.prediction?.action || 'N/A'}
                          {' → '}
                          Actual: {miss.outcome?.new_stage || 'Unknown'}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
